import { License } from '../../../lib/interfaces/license';
import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AppdataService } from '../../../service/appdata.service';
import { CompaniesService } from '../../../service/companies.service';
import { StatusList } from '../../../interfaces/enumStatusType';
import { Company, CompanyStructured, CompanyType } from '../../../lib/interfaces/company';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { smallDialogWidth } from '../../../../environments/environment';
import { AvatarEditComponent } from '../../home-header/user-avatar/avatar-edit/avatar-edit.component';
import { GenericStreamsService } from '../../../service/generic-streams.service';
import { SafeUrl } from '@angular/platform-browser';
import { SnackService } from '../../../service/snack.service';

@Component({
    selector: 'app-add-company',
    standalone: false,
    templateUrl: './add-company.component.html',
    styleUrl: './add-company.component.scss'
})
export class AddCompanyComponent implements OnInit, AfterViewInit {
    // Injections
    readonly data = inject<any>(MAT_DIALOG_DATA);
    readonly dialogRef = inject(MatDialogRef<AddCompanyComponent>);
    cd = inject(ChangeDetectorRef)
    appData = inject(AppdataService);
    fb = inject(FormBuilder);
    companyService = inject(CompaniesService);
    private activatedRoute = inject(ActivatedRoute);
    private destroyRef = inject(DestroyRef);
    private dialog = inject(MatDialog);
    private router = inject(Router);
    genService = inject(GenericStreamsService);
    private snack = inject(SnackService);

    // Variables
    isNew: boolean = false;
    subOrganisation: boolean = false;
    statusList = StatusList;
    loading: boolean = false;
    croppedImage: SafeUrl = '';

    // Objects
    companyTypes: CompanyType[] = [];
    industries: any[] = [];
    licenses: any[] = [];
    organisations: CompanyStructured[] = [];
    selectedLicense: any = null;
    globalStreams: { name: string, id: string, status: string }[] = [];
    selectedStream: string = '';

    company = this.fb.group({
        companyId: [this.data?.companyId || null],
        parentId: [this.data?.parentId || null],
        whiteLabelId: [this.data?.whiteLabelId || null],
        companyTypeId: [this.data?.companyTypeId || null, Validators.required],
        userGates: [this.data?.userGates || false],
        customLoginForm: [this.data?.customLoginForm || false],
        industryId: [this.data?.industryId || null],
        webpage: [this.data?.webpage || null],
        name: [this.data?.name || null, Validators.required],
        mission: [this.data?.mission || null],
        status: [this.data?.status || 'ACTIVE'],
        makesPayment: [this.data?.makesPayment || false],
        isoCountryCode: [this.data?.isoCountryCode || 'de'],
        licenseTypeId: [''],
        privateStreams: [this.data?.privateStreams as (string)[] || []],
    });

    public async ngOnInit(): Promise<void> {
        this.activatedRoute.queryParamMap
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (params) => {
                    if (params.get('edit-logo')) {
                        this.editCompanyLogo();
                    }
                }
            });
        let result = await this.genService.getPrivateGenericStreams();
        if (result !== false) {
            this.globalStreams = result;
            console.log(this.globalStreams);

        }
    }
    getNameOfStream(streamId: string): string {
        const stream = this.globalStreams.find(s => s.id === streamId);
        return stream ? stream.name : 'Unknown Stream';
    }
    async addStream() {
        if (this.selectedStream) {
            const stream = this.company.value.privateStreams?.find(s => s === this.selectedStream);
            if (!stream) {
                this.company.value.privateStreams?.push(this.selectedStream);
            }
        }
    }
    removeStream(streamId: string) {
        this.company.value.privateStreams = this.company.value.privateStreams?.filter(s => s !== streamId);
    }
    async saveCompany() {
        if (!this.company.value.companyTypeId ||
            !this.company.value.name ||
            !this.company.value.licenseTypeId ||
            this.company.value.licenseTypeId.trim() === "") {
            console.log('Company data is invalid:', this.company);

            this.snack.error({
                headline: 'Missing required fields',
                text: 'Please fill out Name, Company type and License before saving.',
            });

            this.company.markAsTouched();
            this.cd.detectChanges();
            return;
        }
        const companyData = this.company.value;
        let result = false;
        console.log(companyData, this.selectedLicense);
        this.loading = true;
        if (this.isNew === true) {
            // Add new Organisation / Company
            result = await this.companyService.addNewCompany(companyData as Company, this.selectedLicense);
            if (result && this.croppedImage) {
                let resultImage = await this.companyService.uploadLogo(<string>this.croppedImage, String(result));
            }
        } else {
            // Update Organisation / Company
            result = await this.companyService.updateCompany(companyData as Company, this.selectedLicense);
        }
        this.loading = false;
        if (result === false) {
            return;
        }
        this.dialogRef.close(true);
    }

    async onLicenseChange(event: Event) {
        this.selectedLicense = await this.licenses.find(license => license.id === event);
    }

    async ngAfterViewInit(): Promise<void> {
        this.appData.companyTypes.subscribe(d => {
            if (d && d.length > 0) {
                this.companyTypes = d;
            }
        })
        this.industries = this.appData.industryTypes;
        this.licenses = this.appData.licenseTypes;
        this.isNew = this.data.companyId ? false : true;
        this.cd.detectChanges();
        if (this.data.companyId) await this.getLicenseForCompany(this.data.companyId);
        if (this.data.parentId && !this.data.companyId) {
            this.organisations = await this.companyService.getCompanyStructureById(this.data.parentId);
            this.subOrganisation = true;
            this.cd.detectChanges();
        }
    }

    async getLicenseForCompany(companyId: string) {
        const li = await this.companyService.getLicenseByCompanyId(companyId);
        if (li === false || !li) return;

        this.selectedLicense = await this.licenses.find((l: License) => l.id === li.licenseTypeId);
        this.selectedLicense.sublicense.count = li.totalSeats;
        this.selectedLicense.credits = li.credits;
        this.company.value.licenseTypeId = li.licenseTypeId;
    }

    cancel(event: Event) {
        event.preventDefault();
        this.dialogRef.close();
    }

    protected editCompanyLogo(): void {
        const result = this.dialog.open(AvatarEditComponent, {
            width: smallDialogWidth,
            panelClass: 'avatar-edit-dialog',
            data: this.data,
        });

        result.afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (image: any) => {
                    if (this.isNew === true) {
                        this.croppedImage = image;
                    } else {
                        this.data.logo = true;
                        this.data.refreshToken = new Date().getTime();
                    }

                    this.router.navigate([], {
                        relativeTo: this.activatedRoute,
                        queryParams: { 'edit-logo': null },
                        queryParamsHandling: 'merge'
                    });
                }
            });
    }
}
